import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import Accordion from '@classify/react-core-components/Accordion';
import Box from '@classify/react-core-components/Box';
import type { SxProps, Theme } from '@classify/react-core-components/styles';
import DoneIcon from '@mui/icons-material/Done';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import EmbeddedChapterContent from './components/EmbeddedChapterContent';
import EmbeddedChapterLoadErrorCard from './components/EmbeddedChapterLoadErrorCard';
import EmbeddedChapterHeading from './components/EmbeddedChapterHeading';
import useEmbeddedChapterCompletion from '../../hooks/useEmbeddedChapterCompletion';
import useEmbeddedChapterData from '../../hooks/useEmbeddedChapterData';
import { getEmbeddedChapterKey } from '../../services/expandableEmbeddedChapter';
import { type ExpandableEmbeddedChapterProps } from '../../types';
const accordionSx: SxProps<Theme> = theme => ({
  borderRadius: 2,
  width: '100%',
  boxSizing: 'border-box',
  marginBottom: theme.spacing(4),
  boxShadow: theme.shadows[2]
});
const ExpandableEmbeddedChapter = t0 => {
  const $ = _c(61);
  const {
    client,
    href,
    linkLabel,
    courseId,
    chapterId,
    previousEmbeddedChapters: t1,
    translations: t2,
    parseRichContent
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const previousEmbeddedChapters = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const translations = t4;
  let t5;
  if ($[4] !== chapterId || $[5] !== client || $[6] !== courseId || $[7] !== href || $[8] !== linkLabel || $[9] !== previousEmbeddedChapters) {
    t5 = {
      client,
      href,
      linkLabel,
      courseId,
      chapterId,
      previousEmbeddedChapters
    };
    $[4] = chapterId;
    $[5] = client;
    $[6] = courseId;
    $[7] = href;
    $[8] = linkLabel;
    $[9] = previousEmbeddedChapters;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    isLoading,
    chapterContent,
    hasFailedLoading,
    completionState,
    setCompletionState,
    chapterTitle,
    isAlreadyCompletedByUser,
    setIsAlreadyCompletedByUser,
    previousEmbeddedCompletionById
  } = useEmbeddedChapterData(t5);
  let t6;
  if ($[11] !== chapterId || $[12] !== client || $[13] !== setCompletionState || $[14] !== setIsAlreadyCompletedByUser) {
    t6 = {
      client,
      chapterId,
      setCompletionState,
      setIsAlreadyCompletedByUser
    };
    $[11] = chapterId;
    $[12] = client;
    $[13] = setCompletionState;
    $[14] = setIsAlreadyCompletedByUser;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    handleMarkAsComplete
  } = useEmbeddedChapterCompletion(t6);
  const chapterContentRef = useRef(null);
  const chapterTitleRef = useRef(null);
  let t7;
  let t8;
  if ($[16] !== chapterContent || $[17] !== client || $[18] !== isLoading || $[19] !== parseRichContent) {
    t7 = () => {
      if (isLoading || !chapterContent || !chapterContentRef.current || !parseRichContent) {
        return;
      }
      parseRichContent(chapterContentRef.current, client, undefined, null, {});
    };
    t8 = [chapterContent, client, isLoading, parseRichContent];
    $[16] = chapterContent;
    $[17] = client;
    $[18] = isLoading;
    $[19] = parseRichContent;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  useEffect(t7, t8);
  const shouldStartCollapsed = isAlreadyCompletedByUser || previousEmbeddedChapters.some(embeddedChapter => {
    const embeddedChapterKey = getEmbeddedChapterKey(embeddedChapter);
    const isCompleted = previousEmbeddedCompletionById[embeddedChapterKey] === true;
    return !isCompleted;
  });
  const defaultExpandedIndex = shouldStartCollapsed ? undefined : 0;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <RotateRightIcon sx={{
      color: "text.onMainMinor"
    }} fontSize="small" />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const defaultHeadingIcon = t9;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[23] !== chapterTitle || $[24] !== completionState || $[25] !== defaultExpandedIndex || $[26] !== hasFailedLoading || $[27] !== href || $[28] !== isLoading || $[29] !== translations) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const renderFramedHeading = t16 => {
        const icon = t16 === undefined ? defaultHeadingIcon : t16;
        return <EmbeddedChapterHeading icon={icon} title={chapterTitle} titleRef={chapterTitleRef} />;
      };
      if (isLoading) {
        t15 = null;
        break bb0;
      }
      if (hasFailedLoading) {
        let t17;
        if ($[38] !== href || $[39] !== translations) {
          t17 = <EmbeddedChapterLoadErrorCard href={href} translations={translations} />;
          $[38] = href;
          $[39] = translations;
          $[40] = t17;
        } else {
          t17 = $[40];
        }
        t15 = <Box data-chapter-inline-expansion="true"><Accordion expandAriaLabel={translations.expandAriaLabel || ""} defaultExpandedIndex={0} sx={accordionSx} items={[{
            title: renderFramedHeading(),
            disableDefaultTitleStyles: true,
            disableDefaultContentStyles: true,
            content: t17
          }]} /></Box>;
        break bb0;
      }
      let t17;
      if ($[41] !== completionState) {
        t17 = completionState === "completed" ? <DoneIcon sx={{
          color: "success.main"
        }} fontSize="small" /> : defaultHeadingIcon;
        $[41] = completionState;
        $[42] = t17;
      } else {
        t17 = $[42];
      }
      const headingIcon = t17;
      T1 = Box;
      t14 = "true";
      T0 = Accordion;
      t11 = translations.expandAriaLabel || "";
      t12 = defaultExpandedIndex;
      t13 = accordionSx;
      t10 = renderFramedHeading(headingIcon);
    }
    $[23] = chapterTitle;
    $[24] = completionState;
    $[25] = defaultExpandedIndex;
    $[26] = hasFailedLoading;
    $[27] = href;
    $[28] = isLoading;
    $[29] = translations;
    $[30] = T0;
    $[31] = T1;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    T0 = $[30];
    T1 = $[31];
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
    t13 = $[35];
    t14 = $[36];
    t15 = $[37];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[43] !== chapterContent || $[44] !== completionState || $[45] !== handleMarkAsComplete || $[46] !== translations) {
    t16 = <EmbeddedChapterContent chapterContent={chapterContent} chapterContentRef={chapterContentRef} completionState={completionState} onMarkAsComplete={handleMarkAsComplete} translations={translations} />;
    $[43] = chapterContent;
    $[44] = completionState;
    $[45] = handleMarkAsComplete;
    $[46] = translations;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t10 || $[49] !== t16) {
    t17 = [{
      title: t10,
      disableDefaultTitleStyles: true,
      disableDefaultContentStyles: true,
      content: t16
    }];
    $[48] = t10;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== T0 || $[52] !== t11 || $[53] !== t12 || $[54] !== t13 || $[55] !== t17) {
    t18 = <T0 expandAriaLabel={t11} defaultExpandedIndex={t12} sx={t13} items={t17} />;
    $[51] = T0;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== T1 || $[58] !== t14 || $[59] !== t18) {
    t19 = <T1 data-chapter-inline-expansion={t14}>{t18}</T1>;
    $[57] = T1;
    $[58] = t14;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
};
export default ExpandableEmbeddedChapter;